import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn.js';

const MAX = 5;

export const starVariants = cva('shrink-0', {
  variants: {
    size: {
      sm: 'size-3.5',
      md: 'size-[18px]',
      lg: 'size-7',
    },
  },
  defaultVariants: { size: 'md' },
});

type StarSize = NonNullable<VariantProps<typeof starVariants>['size']>;

/** The glyph itself. Fill and edge come from the caller's classes. */
function Star({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className}>
      <path
        d="M12 2.6l2.9 5.88 6.49.95-4.7 4.58 1.11 6.46L12 17.42l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.95L12 2.6z"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface RatingStarsProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** 0–5, fractional. `4.37` draws 4.37 stars, not 4.5 of them. */
  value: number | null;
  size?: StarSize;
  /** Screen-reader text. This tier is untranslated, so the caller supplies it. */
  label?: string;
}

/**
 * A rating, drawn honestly.
 *
 * Two stacked rows of the same glyph — an empty one underneath and a filled
 * one clipped to the exact percentage — so any value renders truthfully at
 * any size, with no half-star asset and no rounding that quietly flatters a
 * 4.4 into a 4.5.
 */
export const RatingStars = forwardRef<HTMLSpanElement, RatingStarsProps>(function RatingStars(
  { value, size = 'md', label, className, ...props },
  ref,
) {
  const filled = Math.max(0, Math.min(MAX, value ?? 0));
  const stars = Array.from({ length: MAX }, (_, index) => index);

  return (
    <span
      ref={ref}
      className={cn('relative inline-flex shrink-0 align-middle', className)}
      {...props}
    >
      <span className="inline-flex gap-0.5" aria-hidden>
        {stars.map((index) => (
          <Star
            key={index}
            className={cn(starVariants({ size }), 'fill-rating-star-empty stroke-transparent')}
          />
        ))}
      </span>
      <span
        className="pointer-events-none absolute inset-0 inline-flex gap-0.5 overflow-hidden"
        style={{ width: `${(filled / MAX) * 100}%` }}
        aria-hidden
      >
        {stars.map((index) => (
          <Star
            key={index}
            className={cn(starVariants({ size }), 'fill-rating-star stroke-rating-star-edge')}
          />
        ))}
      </span>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
});

export interface RatingInputProps {
  value: number | null;
  onChange: (value: number) => void;
  /**
   * One label per star, 1 to 5. Required rather than optional: it is the
   * entire accessible name of each control, and a default in English would
   * quietly ship untranslated into a three-language product.
   */
  labels: readonly [string, string, string, string, string];
  size?: StarSize;
  disabled?: boolean;
  /** `FormField` generates these and hands them in. */
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  className?: string;
}

/**
 * Picking a rating, as a real radio group.
 *
 * Arrow keys move, Home and End jump to the ends, and the digits 1–5 set
 * directly — which is not a flourish: the same rating can be given by
 * replying "4" in WhatsApp, and the two paths should not feel like different
 * products. Selection follows focus, so one arrow press is one rating.
 */
export function RatingInput({
  value,
  onChange,
  labels,
  size = 'lg',
  disabled,
  id,
  name,
  className,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: RatingInputProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const shown = hovered ?? value ?? 0;
  const stars = [1, 2, 3, 4, 5] as const;

  function move(to: number) {
    if (disabled) return;
    onChange(Math.max(1, Math.min(MAX, to)));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const current = value ?? 0;
    if (event.key >= '1' && event.key <= '5') {
      event.preventDefault();
      move(Number(event.key));
      return;
    }
    const step: Record<string, number> = {
      ArrowRight: current + 1,
      ArrowUp: current + 1,
      ArrowLeft: current - 1,
      ArrowDown: current - 1,
      Home: 1,
      End: MAX,
    };
    const next = step[event.key];
    if (next === undefined) return;
    event.preventDefault();
    move(next || 1);
  }

  return (
    <div
      id={id}
      role="radiogroup"
      aria-describedby={describedBy}
      aria-invalid={invalid}
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHovered(null)}
      className={cn('inline-flex items-center gap-1', className)}
    >
      {stars.map((star) => {
        const active = star <= shown;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            name={name}
            aria-checked={value === star}
            aria-label={labels[star - 1]}
            disabled={disabled}
            /*
             * Roving tabindex: one stop for the whole group, so tabbing
             * through a form with six of these does not mean thirty stops.
             */
            tabIndex={value === star || (!value && star === 1) ? 0 : -1}
            onClick={() => move(star)}
            /*
             * Hover preview is suppressed where there is no hover: on a
             * phone a drag across the row would otherwise paint stars the
             * guest never chose.
             */
            onMouseEnter={() => setHovered(star)}
            onFocus={() => setHovered(star)}
            onBlur={() => setHovered(null)}
            className={cn(
              'grid size-11 place-items-center rounded-lg transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
              disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-hover',
              '[@media(hover:none)]:hover:bg-transparent',
            )}
          >
            <Star
              className={cn(
                starVariants({ size }),
                'transition-colors',
                active
                  ? 'fill-rating-star stroke-rating-star-edge'
                  : 'fill-rating-star-empty stroke-transparent',
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
